import type { SVGProps } from 'react'

/**
 * 图标是手写内联 SVG，不引第三方图标库：这是内网自托管应用，dist 必须自包含，
 * 一个图标集依赖会带进上千个用不到的组件。全部统一 1.6 描边、24 视窗、
 * currentColor，尺寸由 --icon token 控制，因此颜色永远跟随所在层的语义色。
 *
 * 图标一律 aria-hidden：它们只出现在有可见文字的控件旁边，不承担信息。
 */

type IconProps = SVGProps<SVGSVGElement> & { size?: number }

function Svg({ size = 16, children, ...rest }: IconProps) {
  return (
    <svg
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  )
}

export const IconDashboard = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 13h6V4H4zM14 20h6v-9h-6zM4 20h6v-4H4zM14 8h6V4h-6z" />
  </Svg>
)

export const IconIntake = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3v10m0 0 4-4m-4 4-4-4" />
    <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
  </Svg>
)

export const IconInventory = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 7.5 12 3l9 4.5-9 4.5z" />
    <path d="M3 12.5 12 17l9-4.5M3 17 12 21.5 21 17" />
  </Svg>
)

export const IconSearch = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4.5 4.5" />
  </Svg>
)

export const IconBatches = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1" />
    <path d="M3 4v4h4" />
    <path d="M12 7.5V12l3 1.8" />
  </Svg>
)

export const IconRetail = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" />
    <path d="M9.5 8h5M9.5 12h5" />
  </Svg>
)

export const IconAudit = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3 5 6v5.5c0 4.3 2.9 8.2 7 9.5 4.1-1.3 7-5.2 7-9.5V6z" />
    <path d="m9.2 12 2 2 3.6-3.8" />
  </Svg>
)

export const IconSystem = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="4" width="18" height="7" rx="1.6" />
    <rect x="3" y="13" width="18" height="7" rx="1.6" />
    <path d="M6.8 7.5h.01M6.8 16.5h.01" />
  </Svg>
)

export const IconSettings = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
    <circle cx="16" cy="7" r="2.2" />
    <circle cx="10" cy="17" r="2.2" />
  </Svg>
)

export const IconDraft = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3.5" y="4" width="17" height="16" rx="1.8" />
    <path d="M3.5 9h17M3.5 14h17M9 4v16M14.5 4v16" />
  </Svg>
)

export const IconClose = (p: IconProps) => (
  <Svg {...p}>
    <path d="m6 6 12 12M18 6 6 18" />
  </Svg>
)

export const IconInbox = (p: IconProps) => (
  <Svg {...p} size={p.size ?? 28} strokeWidth={1.3}>
    <path d="M3 13.5 5.6 5A2 2 0 0 1 7.5 3.6h9A2 2 0 0 1 18.4 5L21 13.5V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <path d="M3 13.5h5l1.2 2.2h5.6L16 13.5h5" />
  </Svg>
)

export const IconEye = (p: IconProps) => (
  <Svg {...p}>
    <path d="M2.5 12S6 5.8 12 5.8 21.5 12 21.5 12 18 18.2 12 18.2 2.5 12 2.5 12" />
    <circle cx="12" cy="12" r="2.8" />
  </Svg>
)

export const IconEyeOff = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 4.5 20 20" />
    <path d="M9.6 6.2A9.6 9.6 0 0 1 12 5.8c6 0 9.5 6.2 9.5 6.2a17 17 0 0 1-3.3 4" />
    <path d="M6.4 8.1A16.7 16.7 0 0 0 2.5 12S6 18.2 12 18.2a9.7 9.7 0 0 0 3.7-.7" />
    <path d="M10.2 10.3a2.8 2.8 0 0 0 3.7 3.9" />
  </Svg>
)

// 默认朝左（收起侧栏）；折叠后用 CSS 旋转 180°，按钮位置就不用跳来跳去。
export const IconChevron = (p: IconProps) => (
  <Svg {...p}>
    <path d="m14.5 6-5.5 6 5.5 6" />
  </Svg>
)

export const IconHuntly = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4 4" />
  </Svg>
)

export const IconFusion = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3 4 7.5 12 12l8-4.5L12 3Z" />
    <path d="m4 12 8 4.5L20 12" />
    <path d="m4 16.5 8 4.5 8-4.5" />
  </Svg>
)

export const IconExtract = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 5h16l-6.2 7.2V19l-3.6 1.6v-8.4L4 5Z" />
  </Svg>
)

export const IconFinish = (p: IconProps) => (
  <Svg {...p}>
    <path d="M5 12.5 9.2 17 19 7" />
    <path d="M4 5h16M4 19h10" />
  </Svg>
)

export const IconLogout = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14.5 4.5h-8a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h8" />
    <path d="M17 8.5 20.5 12 17 15.5M20.5 12h-9" />
  </Svg>
)
